import { Modal } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { AppButton } from '../AppButton/AppButton';
import { X } from 'react-feather';
import { COLORS } from '../../../utils/colors';
import { Eye } from 'phosphor-react';
import { AppDropDown } from '../AppDropDown/AppDropDown';
import { AppDropDown2 } from '../AppDropDown2/AppDropDown2';
import moment from 'moment';
import { useAppSelector } from '../../../redux/store';
import {
  PDFFieldReleaseTemplate,
  FromFieldTypeReleaseTemplate,
  ReleaseFieldData,
  NoticeTemplate,
  PreliminaryNoticeFieldData,
  SupplierJobApiResponse,
  FromFieldTypeNoticeTemplate,
  PDFFieldNoticeTemplate,
  formatMoney,
  PrelienStatus,
  SupplierJob,
} from '@dill/dill-shared';
import {
  removePDFFields,
  disablePDFFields,
  structureTemplateNewFieldFromTypePDFData,
  structureTemplateNonNewFieldFromTypePDFData,
  populatePagesPdfWithFieldsData,
} from '../../../utils/helpers';
import { AppInputField } from '../AppInputField/AppInputField';
import { AppVirtualizedList } from '../AppVirtualizedList/AppVirtualizedList';

interface ProcessedPreLienData {
  jobId: string;
  templateId: string;
}

export const AppBulkPreliminaryModal = ({
  open,
  handleClose,
  onPreviewRelease,
  onCreateBulkReleases,
  onSignPreLien,
  signature,
  bulkSaving,
  onEditSupplierJob,
}: {
  open: boolean;
  handleClose: () => void;
  onCreateBulkReleases: (
    data: {
      jobId: string;
      previewPdfUrl: string;
      templateId: string;
      fieldsData: PreliminaryNoticeFieldData[];
      supplierId: string;
      status: string;
      ownersStatus: PrelienStatus[];
      contractorsStatus: PrelienStatus[];
      lendersStatus: PrelienStatus[];
      suretiesStatus?: PrelienStatus[];
    }[]
  ) => void;
  onPreviewRelease: (url: string) => void;
  onSignPreLien: () => void;
  signature: string;
  bulkSaving: boolean;
  onEditSupplierJob: (data: SupplierJobApiResponse) => void;
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { selectedSupplierJobs, supplierJobsMap } = useAppSelector((state) => state.supplierJobs);
  const { noticeTemplates } = useAppSelector((state) => state.noticeTemplates);

  const [error, setError] = useState<{ [key: number]: string[] }[]>([]);
  const [missingDetailsError, setMissingDetailsError] = useState<{ [key: number]: string[] }[]>([]);
  const [previewLoading, setPreviewLoading] = useState<null | number>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processedPreLienNotices, setProcessedPreLienNotices] = useState<ProcessedPreLienData[]>(
    []
  );

  const columns = useMemo(() => {
    const currentColumns = [
      { id: 'jobId', name: 'Job', width: 180, cellType: 'VIEW_TEXT' },
      { id: 'templateId', name: 'Preliminary Notice Template', width: 180, cellType: 'VIEW_TEXT' },
      { id: 'action', name: '', width: 80, cellType: 'VIEW_TEXT' },
    ];
    return currentColumns;
  }, [processedPreLienNotices]);

  const templates = useMemo(() => {
    if (noticeTemplates.length > 0) {
      const processedNoticeTemplates = noticeTemplates.filter(
        (template) => template.status === 'processed'
      );
      return processedNoticeTemplates;
    }
    return [];
  }, [noticeTemplates]);

  const activeJobs = useMemo(() => {
    return Object.values(supplierJobsMap).filter((job) => {
      if (job?.archivedState && job?.archivedState.includes('SUPPLIER_ARCHIVED')) {
        return false;
      }
      return true;
    });
  }, [supplierJobsMap]);

  const totalWidth = useMemo(
    () =>
      columns.reduce((curr, prev) => {
        return curr + prev.width;
      }, 0),
    [columns]
  );

  useEffect(() => {
    if (open) {
      setError([]);
      setMissingDetailsError([]);
    } else {
      setProcessedPreLienNotices([]);
    }
  }, [open]);

  const populatePdf = async (
    fieldDetails: PDFFieldReleaseTemplate[],
    selectedTemplate: NoticeTemplate
  ) => {
    if (!selectedTemplate?.originalPdf?.url) {
      return '';
    }

    let pdfUrl = '';
    const flatPDF = await removePDFFields(selectedTemplate?.originalPdf?.url ?? '');
    pdfUrl = flatPDF ?? '';
    const populatedPdfUrl = await populatePagesPdfWithFieldsData({
      fieldDetails,
      url: pdfUrl,
    });
    if (populatedPdfUrl) {
      return populatedPdfUrl;
    }
    return '';
  };

  const populateAllPdfData = async (
    selectedJob: SupplierJobApiResponse,
    selectedTemplate: NoticeTemplate,
    formFieldsData: {
      [x: string]: PreliminaryNoticeFieldData;
    }
  ) => {
    const specialFieldsPDFData: { fromFieldType: FromFieldTypeNoticeTemplate; dataToUse: any }[] =
      [];

    const dynamicForFieldsPdfData: {
      fromFieldId: string;
      value: any;
    }[] = [];

    const selectedTemplateFields = [...selectedTemplate.formFields]
      .sort((A, B) => {
        if (
          (A.position === null || A.position === undefined) &&
          B.position !== null &&
          B.position !== undefined
        ) {
          return 1;
        }
        if (
          (B.position === null || B.position === undefined) &&
          A.position !== null &&
          A.position !== undefined
        ) {
          return -1;
        }
        if (
          A.position !== null &&
          A.position !== undefined &&
          B.position !== null &&
          B.position !== undefined &&
          A.position > B?.position
        ) {
          return 1;
        }
        return -1;
      })
      .filter(
        (formFields) =>
          formFields.fromFieldType === 'NEW_FIELD' && formFields.whoPopulatesField === 'SUPPLIER'
      );

    if (selectedJob) {
      const filteredSelectedJob = { ...selectedJob };
      const allNewFormFields = { ...formFieldsData };
      Object.keys(allNewFormFields).forEach((formFieldKey) => {
        if (
          allNewFormFields[formFieldKey].value !== 'X' &&
          allNewFormFields[formFieldKey].id === 'checkContractor' &&
          filteredSelectedJob.GCZipCode
        ) {
          filteredSelectedJob.GC = '';
          filteredSelectedJob.GCAddress = '';
          filteredSelectedJob.GCCity = '';
          filteredSelectedJob.GCState = '';
          filteredSelectedJob.GCZipCode = null;
        }
        if (
          allNewFormFields[formFieldKey].value !== 'X' &&
          allNewFormFields[formFieldKey].id === 'checkLender' &&
          filteredSelectedJob.lenderZipCode
        ) {
          delete filteredSelectedJob.lender;
          delete filteredSelectedJob.lenderAddress;
          delete filteredSelectedJob.lenderCity;
          delete filteredSelectedJob.lenderState;
          delete filteredSelectedJob.lenderZipCode;
        }
        if (
          allNewFormFields[formFieldKey].value !== 'X' &&
          allNewFormFields[formFieldKey].id === 'checkOwner'
        ) {
          filteredSelectedJob.owner = '';
          filteredSelectedJob.ownerAddress = '';
          filteredSelectedJob.ownerCity = '';
          filteredSelectedJob.ownerState = '';
          filteredSelectedJob.ownerZipCode = null;
        }
      });
      if (filteredSelectedJob?.estimatedMaterialAmount) {
        filteredSelectedJob.estimatedMaterialAmount = formatMoney(
          filteredSelectedJob.estimatedMaterialAmount,
          2,
          '.',
          ',',
          ''
        );
      }
      if (filteredSelectedJob?.lender === '') {
        filteredSelectedJob.lender = 'N/A';
      }
      specialFieldsPDFData.push({
        fromFieldType: 'SUPPLIER_JOB_FIELD',
        dataToUse: filteredSelectedJob,
      });
    }

    if (signature) {
      specialFieldsPDFData.push({ fromFieldType: 'SIGNATURE_FIELD', dataToUse: signature });
      if (selectedTemplateFields) {
        const foundField = [...selectedTemplateFields].find((field) => field.id === 'signedDate');
        if (foundField) {
          dynamicForFieldsPdfData.push({
            fromFieldId: 'signedDate',
            value: new Date(),
          });
        }
      }
    }

    if (selectedTemplate) {
      const allNewFormFields = { ...formFieldsData };
      Object.keys(allNewFormFields).forEach((formFieldKey) => {
        if (allNewFormFields[formFieldKey].id !== 'signedDate') {
          dynamicForFieldsPdfData.push({
            fromFieldId: formFieldKey,
            value: allNewFormFields[formFieldKey].value,
            ...(allNewFormFields[formFieldKey].fontSize
              ? { fontSize: allNewFormFields[formFieldKey].fontSize }
              : {}),
          });
        }
      });
    }

    if (user) {
      specialFieldsPDFData.push({
        fromFieldType: 'USER_FIELD',
        dataToUse: user,
      });
    }

    if (user) {
      specialFieldsPDFData.push({
        fromFieldType: 'USER_SUPPLIER_FIELD',
        dataToUse: user,
      });
    }

    let pdfData: PDFFieldNoticeTemplate[] = [];
    if (specialFieldsPDFData.length > 0 && selectedTemplate) {
      const data1 = structureTemplateNonNewFieldFromTypePDFData({
        template: selectedTemplate,
        details: specialFieldsPDFData,
      });
      pdfData = [...pdfData, ...data1];
    }
    if (dynamicForFieldsPdfData.length > 0) {
      const data2 = structureTemplateNewFieldFromTypePDFData({
        template: selectedTemplate,
        details: dynamicForFieldsPdfData,
      });
      pdfData = [...pdfData, ...data2];
    }
    const pdfUrl = await populatePdf(pdfData, selectedTemplate);
    return pdfUrl;
  };

  const populateTemplateFieldData = (
    selectedTemplate: NoticeTemplate,
    preliminaryNotice: ProcessedPreLienData,
    selectedJob?: SupplierJob | null
  ) => {
    let data: { [x: string]: PreliminaryNoticeFieldData } = {};
    if (selectedTemplate) {
      selectedTemplate.formFields.forEach((formField) => {
        if (formField.fromFieldType === 'NEW_FIELD' && formField.whoPopulatesField === 'SUPPLIER') {
          let defaultValue: any = '';
          if (
            formField.fieldType === 'CURRENCY' &&
            formField.defaultValue !== null &&
            formField.defaultValue !== undefined
          ) {
            defaultValue = Number(formField.defaultValue).toFixed(2);
          } else if (formField.defaultValue !== null && formField.defaultValue !== undefined) {
            defaultValue = `${formField.defaultValue}`;
          } else {
            defaultValue = formField.defaultValue;
          }
          data = {
            ...data,
            [formField.id]: {
              isRequired: formField.isFieldRequired,
              value:
                formField.defaultValue !== null && formField.defaultValue !== undefined
                  ? defaultValue
                  : '',
              id: formField.id,
              name: formField.fieldName,
              type: formField.fieldType,
            },
          };
        }
      });

      let updateFieldData: { [x: string]: PreliminaryNoticeFieldData } = {};

      for (const key of Object.keys(data)) {
        if (key === 'contractors') {
          const contractors =
            selectedJob?.GCs?.filter((contractor) => contractor.name !== '')?.map(
              (contractor) =>
                `${contractor.name} - ${contractor.address} ${contractor.city} ${contractor.state} ${contractor.zipCode}`
            ) ?? [];
          updateFieldData = {
            ...updateFieldData,
            [key]: {
              ...data[key],
              value:
                selectedJob?.isGCUnknown && contractors.length === 0
                  ? 'GC unknown - unable to identify after due diligence'
                  : contractors.join('\n'),
              fontSize: 10,
            },
          };
        }
        if (key === 'owners') {
          const owners =
            selectedJob?.owners
              ?.filter((owner) => owner.name !== '')
              ?.map(
                (owner) =>
                  `${owner.name} - ${owner.address} ${owner.city} ${owner.state} ${owner.zipCode}`
              ) ?? [];
          updateFieldData = {
            ...updateFieldData,
            [key]: {
              ...data[key],
              value:
                selectedJob?.isOwnerUnknown && owners.length === 0
                  ? 'Owner unknown - unable to identify after due diligence'
                  : owners.join('\n'),
              fontSize: 10,
            },
          };
        }
        if (key === 'lenders') {
          const lenders =
            selectedJob?.lenders
              ?.filter((lender) => lender.name !== '')
              ?.map(
                (lender) =>
                  `${lender.name} - ${lender.address} ${lender.city} ${lender.state} ${lender.zipCode}`
              ) ?? [];
          updateFieldData = {
            ...updateFieldData,
            [key]: {
              ...data[key],
              value:
                selectedJob?.isLenderUnknown && lenders.length === 0
                  ? 'Lender unknown - unable to identify after due diligence'
                  : lenders.length === 0
                  ? 'N/A'
                  : lenders.join('\n'),
              fontSize: 10,
            },
          };
        }
        if (key === 'sureties') {
          const sureties =
            selectedJob?.sureties
              ?.filter((surety) => surety.name !== '')
              ?.map(
                (surety) =>
                  `${surety.name} - ${surety.address} ${surety.city} ${surety.state} ${surety.zipCode}`
              ) ?? [];
          updateFieldData = {
            ...updateFieldData,
            [key]: {
              ...data[key],
              value:
                selectedJob?.isSurityUnknown && sureties.length === 0
                  ? 'Surety unknown - unable to identify after due diligence'
                  : sureties.join('\n'),
              fontSize: 10,
            },
          };
        }
      }

      data = { ...data, ...updateFieldData };
    }
    return { ...data };
  };

  const checkTemplateRequirement = (selectedTemplate: NoticeTemplate) => {
    const requiredField: any[] = [];
    if (selectedTemplate) {
      let data: { [x: string]: PreliminaryNoticeFieldData } = {};
      selectedTemplate.formFields.forEach((formField) => {
        if (formField.fromFieldType === 'NEW_FIELD' && formField.whoPopulatesField === 'SUPPLIER') {
          let defaultValue: any = '';
          if (
            formField.fieldType === 'CURRENCY' &&
            formField.defaultValue !== null &&
            formField.defaultValue !== undefined
          ) {
            defaultValue = Number(formField.defaultValue).toFixed(2);
          } else if (formField.defaultValue !== null && formField.defaultValue !== undefined) {
            defaultValue = `${formField.defaultValue}`;
          } else {
            defaultValue = formField.defaultValue;
          }
          data = {
            ...data,
            [formField.id]: {
              isRequired: formField.isFieldRequired,
              value:
                formField.defaultValue !== null && formField.defaultValue !== undefined
                  ? defaultValue
                  : '',
              id: formField.id,
              name: formField.fieldName,
              type: formField.fieldType,
            },
          };
        }
      });
    }
    return requiredField;
  };

  useEffect(() => {
    const fetchedData = [];
    for (let index = 0; index < selectedSupplierJobs.length; index++) {
      const selectedSupplierJob = selectedSupplierJobs[index];
      const templateIds = templates
        .filter((template) => {
          if (
            template?.state &&
            selectedSupplierJob.jobState &&
            template.state.toUpperCase() === selectedSupplierJob.jobState.toUpperCase()
          ) {
            return true;
          }
          return false;
        })
        .map((template) => template);

      fetchedData.push({
        templateId: templateIds.length > 0 ? templateIds[0].id : '',
        jobId: selectedSupplierJob.id,
      });
    }
    if (open) {
      setProcessedPreLienNotices(fetchedData);
    }
  }, [selectedSupplierJobs, templates, open]);

  const handleCloseModal = () => {
    setProcessedPreLienNotices([]);
    setError([]);
    setMissingDetailsError([]);
    handleClose();
  };

  const handleChangeValue = (key: keyof ProcessedPreLienData, index: number, value: string) => {
    setError([]);
    const updateData = [...processedPreLienNotices];
    updateData[index][key] = value;
    setProcessedPreLienNotices(updateData);
  };

  const errorData = (data: ProcessedPreLienData, template?: NoticeTemplate) => {
    let tempRequirement: string[] = [];
    if (template) {
      tempRequirement = checkTemplateRequirement(template);
    }
    const errorsFound: string[] = [];
    if (data.jobId === '') {
      errorsFound.push('Job');
    }
    if (data.templateId === '') {
      errorsFound.push('Preliminary Notice Template');
    }
    return errorsFound;
  };

  const checkMissingJobDetails = (job?: SupplierJobApiResponse | null) => {
    if (job) {
      const detailsMissingList: string[] = [];
      if (!job.isOwnerUnknown && job?.owners?.filter((own) => own.name !== '')?.length === 0) {
        detailsMissingList.push('Owner');
      }
      if (!job.isGCUnknown && job?.GCs?.filter((gc) => gc.name !== '')?.length === 0) {
        detailsMissingList.push('GC');
      }
      if (!job?.estimatedMaterialAmount) {
        detailsMissingList.push('Estimated Material Amount');
      }
      return detailsMissingList;
    }
    return [];
  };

  const handlePreview = async (index: number, data: ProcessedPreLienData) => {
    setError([]);
    setMissingDetailsError([]);
    const temp = templates.find((tmp) => tmp.id === data.templateId);
    const errorsFound = errorData(data, temp);
    if (errorsFound.length > 0) {
      setPreviewLoading(null);
      setError([{ [index]: errorsFound }]);
      return;
    }
    const job = activeJobs.find((jb) => jb.id === data.jobId);

    const missingJobDetails = checkMissingJobDetails(job);
    if (missingJobDetails.length > 0) {
      setPreviewLoading(null);
      setMissingDetailsError([{ [index]: missingJobDetails }]);
      return;
    }

    if (temp && job) {
      const tempRequirement = checkTemplateRequirement(temp);
      const populatedTemplateFields = populateTemplateFieldData(temp, data, job);
      const url = await populateAllPdfData(job, temp, populatedTemplateFields);
      const flatPDFUrl = await disablePDFFields(url);
      onPreviewRelease(flatPDFUrl ?? '');
    }
    setPreviewLoading(null);
  };

  const groupRelease = async (data: ProcessedPreLienData[]) => {
    const releaseArray = [];
    for (let index = 0; index < data.length; index++) {
      const preliminaryNotice = data[index];

      const job = activeJobs.find((jb) => jb.id === preliminaryNotice.jobId);
      const temp = templates.find((tmp) => tmp.id === preliminaryNotice.templateId);
      if (temp && job) {
        let additionalData = {};
        if (job?.isJobBonded) {
          additionalData = {
            ...additionalData,
            suretiesStatus:
              job?.sureties
                ?.filter((surety) => surety.name !== '')
                ?.map((surety) => ({
                  name: surety.name,
                  trackLink: '',
                })) ?? [],
          };
        }
        const populatedTemplateFields = populateTemplateFieldData(temp, preliminaryNotice, job);
        const url = await populateAllPdfData(job, temp, populatedTemplateFields);
        const processedPreLienData = {
          jobId: job.id || '',
          previewPdfUrl: url,
          templateId: temp?.id || '',
          fieldsData: Object.values(populatedTemplateFields),
          supplierId: currentSupplier?.id ?? '',
          status: 'pending',
          ownersStatus:
            job?.owners
              ?.filter((own) => own.name !== '')
              ?.map((owner) => ({
                name: owner.name,
                trackLink: '',
              })) ?? [],
          contractorsStatus:
            job?.GCs?.filter((contractor) => contractor.name !== '')?.map((contractor) => ({
              name: contractor.name,
              trackLink: '',
            })) ?? [],
          lendersStatus:
            job?.lenders
              ?.filter((lender) => lender.name !== '')
              ?.map((lender) => ({
                name: lender.name,
                trackLink: '',
              })) ?? [],
          ...additionalData,
        };
        releaseArray.push(processedPreLienData);
      }
    }
    return releaseArray;
  };

  const handleBulkSendReleases = async () => {
    setIsLoading(true);
    setError([]);
    setMissingDetailsError([]);
    const errors: { [key: number]: string[] }[] = [];
    const missingJobDetails: { [key: number]: string[] }[] = [];
    const selectedRelease = [];
    for (let index = 0; index < processedPreLienNotices.length; index++) {
      const preliminaryNotice = processedPreLienNotices[index];
      const temp = templates.find((tmp) => tmp.id === preliminaryNotice.templateId);
      const errorsFound = errorData(preliminaryNotice, temp);
      if (errorsFound.length > 0) {
        errors.push({ [index]: errorsFound });
      }
      const job = activeJobs.find((jb) => jb.id === preliminaryNotice.jobId);
      const missingJobDetailsCheck = checkMissingJobDetails(job);
      if (missingJobDetailsCheck.length > 0) {
        missingJobDetails.push({ [index]: missingJobDetailsCheck });
      }
      selectedRelease.push(preliminaryNotice);
    }
    if (errors.length > 0) {
      setError(errors);
      setIsLoading(false);
      return;
    }
    if (missingJobDetails.length > 0) {
      setMissingDetailsError(missingJobDetails);
      setIsLoading(false);
      return;
    }
    const updateRelease = await groupRelease(selectedRelease);
    setIsLoading(false);
    onCreateBulkReleases(updateRelease);
  };

  const renderRow = (item: { id: string; content: React.ReactNode }) => {
    const index = Number(item.id);
    const data = processedPreLienNotices[index];
    return (
      <div key={index + 'oo'} className="w-full flex  px-4 justify-between items-center">
        {columns.map((column) => {
          const stringIndex = index.toString();
          type ObjectKey = keyof typeof data;
          const columnKey = column.id as ObjectKey;
          const errorList = error.find((itm) => Object.keys(itm)[0] === stringIndex);
          const errorValues = errorList ? Object.values(errorList)[0] : [];
          const temp = templates.find((tmp) => tmp.id === data.templateId);

          const errorMissingJobDetails = missingDetailsError.find(
            (itm) => Object.keys(itm)[0] === stringIndex
          );
          const errorMissingJobDetailsValues = errorMissingJobDetails
            ? Object.values(errorMissingJobDetails)[0]
            : [];

          let checkRequireTempField: string[] = [];
          if (temp) {
            checkRequireTempField = checkTemplateRequirement(temp);
          }
          return (
            <div key={column.id} style={{ flex: column.width / totalWidth }} className="py-3 px-1">
              {column.cellType === 'VIEW_TEXT' && column.id === 'jobId' && (
                <AppDropDown2
                  value={(data as any)[column.id]}
                  errorText={
                    errorValues.includes(column.name)
                      ? `${column.name} is required`
                      : `${
                          errorMissingJobDetailsValues.length > 0
                            ? `Job is missing key information like ${errorMissingJobDetailsValues.join(
                                ', '
                              )}. Please update the job in the dropdown above.`
                            : ''
                        }`
                  }
                  items={[
                    ...activeJobs.map((job) => {
                      return {
                        label: `${job?.jobNumber ? `${job.jobNumber} - ` : ''}${job.name}`,
                        value: job.id,
                        labelInElement: (
                          <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                            <div className="text-xs">{`${
                              job?.jobNumber ? `${job.jobNumber} - ` : ''
                            }${job.name}`}</div>
                            <button
                              onClick={(e) => {
                                onEditSupplierJob(job);
                              }}
                              className="text-xs px-4 py-1 WHITE-BG font-bold TEXT_SECONDARY-CLR rounded absolute hidden group-hover:inline-block right-2">
                              Edit
                            </button>
                          </div>
                        ),
                      };
                    }),
                  ]}
                  onSelectChange={(val) => {
                    if (val) {
                      handleChangeValue(columnKey, index, val.value);
                    }
                  }}
                />
              )}
              {column.cellType === 'VIEW_TEXT' && column.id === 'templateId' && (
                <AppDropDown
                  value={(data as any)[column.id]}
                  errorText={errorValues.includes(column.name) ? `${column.name} is required` : ''}
                  items={[
                    ...templates
                      .filter((template) => {
                        const selectedJob = activeJobs.find((job) => job.id === data.jobId);
                        if (
                          template?.state &&
                          selectedJob?.jobState &&
                          template.state.toUpperCase() === selectedJob.jobState.toUpperCase()
                        ) {
                          return true;
                        }
                        return false;
                      })
                      .map((template) => {
                        return {
                          label: template.name,
                          value: template.id,
                        };
                      }),
                  ]}
                  onSelectChange={(itm) => {
                    if (itm) {
                      handleChangeValue(columnKey, index, itm.value);
                    }
                  }}
                  height={240}
                />
              )}
              {column.cellType === 'VIEW_TEXT' && column.id === 'action' && (
                <div className="flex items-center justify-center w-full">
                  <AppButton
                    type="TERTIARY"
                    buttonStyles={{
                      padding: '8px',
                      margin: '5px 8px',
                    }}
                    icon={<Eye color={COLORS.GREY_500} size={20} />}
                    onClick={() => {
                      setPreviewLoading(index);
                      handlePreview(index, data);
                    }}
                    isLoading={previewLoading === index}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <div className="flex w-[60vw] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg  flex-col">
        <h2 className="text-lg font-semibold mx-4 my-3">Bulk Request Preliminary Notices</h2>
        <X
          className="absolute right-4 top-4 cursor-pointer"
          color={COLORS.GREY_500}
          onClick={handleCloseModal}
        />
        <div style={{ height: '0.01em' }} className="h-0.5 w-full bg-slate-400"></div>
        <div className="mx-4 mt-3 mb-2">
          <div className="flex flex-col items-center w-full PRIMARY_50-BG justify-center p-4">
            <div className="flex items-center w-full mb-2">
              <div className="flex w-full my-2 font-medium text-sm">
                Preliminary notices you want to request
              </div>
            </div>
            <div className="flex w-full flex-col min-h-[40vh]  max-h-[55vh] overflow-y-scroll border WHITE-BG rounded-xl">
              {processedPreLienNotices.length > 0 && (
                <div className="flex border-b px-5 items-center w-full sticky top-0 PRIMARY_50-BG py-3 z-20">
                  {columns.map((column) => {
                    return (
                      <div
                        key={column.id}
                        style={{ flex: column.width / totalWidth }}
                        className="flex w-full">
                        {column.cellType !== 'SELECT' && column.id !== 'select' && (
                          <p className="TEXT_SECONDARY-CLR text-xs w-full text-left">
                            {column.name}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {processedPreLienNotices.length === 0 && (
                <div className="flex w-full h-[50vh] items-center justify-center font-semibold TEXT_SECONDARY-CLR">
                  {`No releases to be requested for this month`}
                </div>
              )}
              <div>
                {processedPreLienNotices.length > 0 && (
                  <div className="h-[50vh]">
                    <AppVirtualizedList
                      itemsLength={processedPreLienNotices.length}
                      itemHeight={70}
                      containerHeight={50}
                      renderRow={renderRow}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div style={{ height: '0.01em' }} className="h-0.5 w-full bg-slate-400 my-4"></div>
        <div className="flex justify-end mx-3 mb-2">
          <AppButton
            text="Cancel"
            type="TERTIARY"
            onClick={handleCloseModal}
            buttonStyles={{ marginRight: '8px', height: '40px' }}
          />
          <AppButton
            text="Bulk Request"
            onClick={handleBulkSendReleases}
            buttonStyles={{
              width: '100px',
              height: '40px',
            }}
            isLoading={bulkSaving || isLoading}
            isDisabled={bulkSaving || isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};
